import React from 'react';
import { CheckCircle, XCircle, User, Mail } from 'lucide-react';

interface LinkedPatientBadgeProps {
  isLinked: boolean;
  linkedUserEmail?: string;
  linkedUserName?: string;
  linkedPatientName?: string;
  size?: 'sm' | 'md' | 'lg';
  showTooltip?: boolean;
}

const LinkedPatientBadge: React.FC<LinkedPatientBadgeProps> = ({
  isLinked,
  linkedUserEmail,
  linkedUserName,
  linkedPatientName,
  size = 'md',
  showTooltip = true
}) => {
  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };

  if (isLinked) {
    const tooltipContent = linkedUserEmail || linkedUserName || linkedPatientName
      ? `Vinculado a: ${linkedUserEmail || linkedUserName || linkedPatientName}`
      : 'Paciente vinculado a usuário';

    return (
      <div className="relative group">
        <span
          className={`inline-flex items-center gap-1.5 ${sizeClasses[size]} bg-green-100 text-green-800 rounded-full font-medium`}
        >
          <CheckCircle className={iconSizes[size]} />
          Vinculado
        </span>

        {showTooltip && (linkedUserEmail || linkedUserName || linkedPatientName) && (
          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
            <div className="flex flex-col gap-1">
              {linkedUserEmail && (
                <div className="flex items-center gap-2">
                  <Mail className="w-3 h-3" />
                  <span>{linkedUserEmail}</span>
                </div>
              )}
              {linkedUserName && (
                <div className="flex items-center gap-2">
                  <User className="w-3 h-3" />
                  <span>{linkedUserName}</span>
                </div>
              )}
              {linkedPatientName && !linkedUserName && (
                <div className="flex items-center gap-2">
                  <User className="w-3 h-3" />
                  <span>{linkedPatientName}</span>
                </div>
              )}
            </div>
            <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
              <div className="border-4 border-transparent border-t-gray-900"></div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative group">
      <span
        className={`inline-flex items-center gap-1.5 ${sizeClasses[size]} bg-gray-100 text-gray-600 rounded-full font-medium`}
      >
        <XCircle className={iconSizes[size]} />
        Não Vinculado
      </span>

      {showTooltip && (
        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-10">
          Este paciente não possui usuário vinculado
          <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
            <div className="border-4 border-transparent border-t-gray-900"></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LinkedPatientBadge;
